import { createTheme } from '@mui/material/styles';

/**
 * Custom MUI theme that reads from shadcn/Tailwind CSS variables.
 * This allows MUI components to match our app's dark theme.
 */
export const createEmailEditorTheme = () => {
  // Helper to get CSS variable value
  const getCSSVar = (varName) => {
    if (typeof window === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };

  return createTheme({
    palette: {
      mode: 'dark',
      background: {
        default: `hsl(${getCSSVar('--background')})`,
        paper: `hsl(${getCSSVar('--card')})`,
      },
      text: {
        primary: `hsl(${getCSSVar('--foreground')})`,
        secondary: `hsl(${getCSSVar('--muted-foreground')})`,
      },
      primary: {
        main: `hsl(${getCSSVar('--primary')})`,
        contrastText: `hsl(${getCSSVar('--primary-foreground')})`,
      },
      secondary: {
        main: `hsl(${getCSSVar('--secondary')})`,
        contrastText: `hsl(${getCSSVar('--secondary-foreground')})`,
      },
      error: {
        main: `hsl(${getCSSVar('--destructive')})`,
        contrastText: `hsl(${getCSSVar('--destructive-foreground')})`,
      },
      divider: `hsl(${getCSSVar('--border')})`,
    },
    typography: {
      fontFamily: 'inherit',
      fontSize: 14,
    },
    shape: {
      borderRadius: 6, // Match shadcn's border radius
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // Remove MUI's default gradient
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none', // Don't uppercase button text
            borderRadius: 6,
          },
          outlined: {
            borderColor: `hsl(${getCSSVar('--border')})`,
            '&:hover': {
              borderColor: `hsl(${getCSSVar('--primary')})`,
              backgroundColor: `hsl(${getCSSVar('--accent')})`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              '& fieldset': {
                borderColor: `hsl(${getCSSVar('--input')})`,
              },
              '&:hover fieldset': {
                borderColor: `hsl(${getCSSVar('--ring')})`,
              },
              '&.Mui-focused fieldset': {
                borderColor: `hsl(${getCSSVar('--ring')})`,
              },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: `1px solid hsl(${getCSSVar('--border')})`,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: `hsl(${getCSSVar('--popover')})`,
            color: `hsl(${getCSSVar('--popover-foreground')})`,
            border: `1px solid hsl(${getCSSVar('--border')})`,
            fontSize: '0.875rem',
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: `hsl(${getCSSVar('--primary')})`,
          },
          thumb: {
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px hsla(${getCSSVar('--primary')} / 0.16)`,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: `hsl(${getCSSVar('--primary-foreground')})`,
            backgroundColor: `hsl(${getCSSVar('--primary')})`,
            border: `1px solid hsl(${getCSSVar('--primary')})`,
            '&:hover': {
              backgroundColor: `hsl(${getCSSVar('--primary')})`,
              opacity: 0.9,
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid hsl(${getCSSVar('--border')})`,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: `hsl(${getCSSVar('--accent')})`,
            },
            '&.Mui-selected': {
              backgroundColor: `hsl(${getCSSVar('--accent')})`,
              '&:hover': {
                backgroundColor: `hsl(${getCSSVar('--accent')})`,
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 48,
            fontWeight: 500,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: `hsl(${getCSSVar('--primary')})`,
          },
        },
      },
    },
  });
};
